import { AfterViewInit, Component, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ChannelService, RCU_PRESSED_DEBOUNCE } from '../../services/channel.service'
import { LocalChannel } from '../../interfaces/local-channel'
import { TimeComponent } from '../../layout/components/time/time.component'
import { ChannelPosition } from './channel-position'
import { debounceTime, Subscription } from 'rxjs'
import { EventService } from '../../services/event.service'
import { ChannelProgressComponent } from '../../layout/components/channel-progress/channel-progress.component'
import { CommonModule, NgIf } from '@angular/common'
import { ChannelTrackData } from '../../interfaces/channel-track-data'
import { ConfigService } from '../../services/config.service'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { TranslateModule } from '@ngx-translate/core'
import moment from 'moment'
import { MomentPipe } from '../../pipes/moment.pipe'
import { RouterService, RouterPage } from '../../services/router.service'
import { ChannelPlayerComponent } from '../../layout/components/channel-player/channel-player.component'
import { TracksMenuV1Component } from '../../layout/components/tracks-menu-v1/tracks-menu-v1.component'
import { PlatformService } from '../../platforms/platform.service'
import { HTVPlatform } from '../../platforms/platform'

@Component({
  selector: 'app-channel',
  standalone: true,
  imports: [
    CommonModule,
    MomentPipe,
    NgIf,
    ChannelPlayerComponent,
    ChannelProgressComponent,
    TimeComponent,
    TranslateModule,
    TracksMenuV1Component,
  ],
  templateUrl: './channel.component.html',
  styleUrls: ['./channel.component.scss'],
  providers: [{ provide: LOCALE_ID, useFactory: () => moment.locale() }],
})
export class ChannelComponent implements OnInit, OnDestroy, AfterViewInit {
  eventSubscription?: Subscription
  channelSubscription?: Subscription

  channel!: LocalChannel
  channelId!: string
  channelAudios!: ChannelTrackData[]
  channelSubtitles!: ChannelTrackData[]

  overlayVisible = false
  hideOverlayTimeout: any

  platformType!: HTVPlatform
  HTVPlatform = HTVPlatform

  @ViewChild(TracksMenuV1Component) tracksMenu!: TracksMenuV1Component

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private channelService: ChannelService,
    private configService: ConfigService,
    private eventService: EventService,
    private routerService: RouterService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.loadChannel(this.channelService.getPendingChannel())
    this.platformType = this.platformService.type
  }

  ngOnDestroy(): void {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout)
    }
    this.channelService.stopChannel()
    this.eventSubscription?.unsubscribe()
    this.channelSubscription?.unsubscribe()
  }

  ngAfterViewInit(): void {
    this.showOverlay()
    this.registerEventListener()
    this.registerChannelListener()
  }

  registerEventListener(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe((key: string) => {
      switch (key) {
        case 'BACK':
          if (this.overlayVisible) {
            this.hideOverlay()
          } else {
            this.routerService.goBack()
          }
          break
        default:
          this.showOverlay()
          break
      }
    })
  }

  registerChannelListener(): void {
    this.channelSubscription = this.channelService.selectedChannel$
      .pipe(debounceTime(RCU_PRESSED_DEBOUNCE))
      .subscribe((channel: LocalChannel | undefined) => {
        if (channel && this.channel != channel) {
          this.loadChannel(channel)
          this.appButtonDirective.forceRefreshSelectedButton()
        }
      })
  }

  loadChannel(channel: LocalChannel): void {
    this.channel = channel
    this.channelId = channel.id
    const pos: ChannelPosition = { left: 0, top: 0, width: 1920, height: 1080 }
    this.channelService.startChannel(this.channel, pos)
    this.showOverlay()
  }

  openChannelGrid(): void {
    this.routerService.goToPage(RouterPage.CHANNEL_GRID)
  }

  showOverlay(): void {
    const overlay = document.getElementById('channel-overlay')
    if (overlay) {
      this.overlayVisible = true
      overlay.style.opacity = '1'
    }
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout)
    }
    this.hideOverlayTimeout = setTimeout(() => {
      this.hideOverlay()
    }, this.configService.channelOverlayDurationInMilliseconds)
  }

  hideOverlay(): void {
    this.tracksMenu.hide()
    this.appButtonDirective.clearSelectedButtons()
    const overlay = document.getElementById('channel-overlay')
    if (overlay) {
      this.overlayVisible = false
      overlay.style.opacity = '0'
    }
  }
}
